import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { ThreadModerationList } from '@/components/admin/corkBoard/ThreadModerationList';
import { DesktopEditThreadTagsPanel } from '@/components/admin/corkBoard/desktop/DesktopEditThreadTagsPanel';
import { DesktopAdminPostPanel } from '@/components/admin/corkBoard/desktop/DesktopAdminPostPanel';
import { AdminSelectDropdown } from '@/components/common/admin/AdminSelectDropdown';
import { GlassCard } from '@/components/shared/redesign';
import { GlassFormInput } from '@/components/admin/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { CORK_BOARD_SORT_OPTIONS } from '@/constants/residents/corkBoardConstants';
import type { useThreadModeration } from '@/hooks/admin/useThreadModeration';
import type { CorkBoardThreadSummary, ThreadSortBy } from '@/types/residents/corkBoard';

interface DesktopThreadModerationViewProps {
  moderation: ReturnType<typeof useThreadModeration>;
  editTagsVisible: boolean;
  editingThread: CorkBoardThreadSummary | null;
  postModalVisible: boolean;
  onOpenEditTags: (thread: CorkBoardThreadSummary) => void;
  onCloseEditTags: () => void;
  onSaveTags: (threadId: string, tagIds: number[]) => void;
  onOpenPostModal: () => void;
  onClosePostModal: () => void;
  onSaveAnnouncement: (data: { title: string; body: string; tagIds: number[] }) => void;
  onDelete: (thread: CorkBoardThreadSummary) => void;
  onViewResponses: (thread: CorkBoardThreadSummary) => void;
}

export const DesktopThreadModerationView: React.FC<DesktopThreadModerationViewProps> = ({
  moderation,
  editTagsVisible,
  editingThread,
  postModalVisible,
  onOpenEditTags,
  onCloseEditTags,
  onSaveTags,
  onOpenPostModal,
  onClosePostModal,
  onSaveAnnouncement,
  onDelete,
  onViewResponses,
}) => {
  const { redesign, semantic } = useAdminColors();

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader
        title="Thread Moderation"
        subtitle="Manage community threads: pin, lock, edit tags, delete, and create new posts."
        showAddButton
        onAddPress={onOpenPostModal}
        addButtonText="New Post"
      />

      <View style={styles.content}>
        <View style={styles.searchRow}>
          <View style={styles.searchInputWrap}>
            <GlassFormInput
              value={moderation.searchInput}
              onChangeText={moderation.setSearchInput}
              placeholder="Search threads..."
              containerStyle={styles.searchInputContainer}
            />
          </View>
          <AdminSelectDropdown
            label="Sort"
            options={CORK_BOARD_SORT_OPTIONS}
            selectedValue={moderation.sortBy}
            onSelect={(value) => moderation.setSortBy(value as ThreadSortBy)}
            variant="anchored"
            triggerStyle={styles.sortTrigger}
          />
        </View>

        {moderation.error && (
          <View
            style={[
              styles.errorBanner,
              {
                backgroundColor: redesign.accent.redBg,
                borderColor: semantic.status.error,
              },
            ]}
          >
            <Text style={[styles.errorBannerText, { color: semantic.status.error }]}>
              Failed to load threads.
            </Text>
            <TouchableOpacity onPress={moderation.refetch}>
              <Text style={[styles.errorRetryText, { color: semantic.status.error }]}>
                Retry
              </Text>
            </TouchableOpacity>
          </View>
        )}

        <GlassCard padding={0} style={styles.card}>
          <View style={[styles.cardHeader, { borderBottomColor: redesign.card.border }]}>
            <Text style={[styles.cardTitle, { color: redesign.text.primary }]}>Threads</Text>
            <Text style={[styles.cardSubtitle, { color: redesign.text.secondary }]}>
              {moderation.totalCount} thread{moderation.totalCount !== 1 ? 's' : ''}
            </Text>
          </View>

          <ThreadModerationList
            threads={moderation.threads}
            isLoading={moderation.isLoading}
            isMutating={moderation.isMutating}
            onPin={moderation.handlePinThread}
            onLock={moderation.handleLockThread}
            onEditTags={onOpenEditTags}
            onDelete={onDelete}
            onViewResponses={onViewResponses}
            onRefresh={moderation.refetch}
            isRefreshing={moderation.isManualRefreshing}
            onLoadMore={moderation.loadMore}
            hasNextPage={moderation.hasNextPage}
            isFetchingMore={moderation.isFetchingNextPage}
          />
        </GlassCard>
      </View>

      <DesktopEditThreadTagsPanel
        visible={editTagsVisible}
        thread={editingThread}
        availableTags={moderation.tags}
        isSaving={moderation.isUpdatingTags}
        onSave={onSaveTags}
        onClose={onCloseEditTags}
      />
      <DesktopAdminPostPanel
        visible={postModalVisible}
        kind="announcement"
        availableTags={moderation.tags}
        isSavingAnnouncement={moderation.isCreatingAnnouncement}
        onSaveAnnouncement={onSaveAnnouncement}
        onClose={onClosePostModal}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
    gap: 16,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
  },
  searchInputWrap: {
    flex: 1,
  },
  searchInputContainer: {
    marginBottom: 0,
  },
  sortTrigger: {
    minHeight: 48,
  },
  card: {
    flex: 1,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  cardSubtitle: {
    fontSize: 13,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  errorBannerText: {
    fontSize: 13,
  },
  errorRetryText: {
    fontSize: 13,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});
